import { BLEND_MODES } from "./constants";
import Color from "./color";
import Gradient from "./gradient";
import Model from "./model";
import ApiFill from "../api/model/snapshots/layers/Fill";
import FillType from "../api/model/snapshots/layers/FillType";
import ColorDefinition from "./ColorDefinition";
import ZeplinBlendMode from "../api/model/snapshots/layers/ZeplinBlendMode";
import { PartialOnly } from "../utils/UtilityTypes";
import ApiGradient from "../api/model/snapshots/layers/Gradient";
import { VariableReference } from "../api/model/variables/Variables";

const fillTypes = ["color", "gradient"];

export default class Fill extends Model {
    #type: FillType;
    #color?: Color;
    #gradient?: Gradient;
    #opacity: number | undefined;
    #blendMode: ZeplinBlendMode | undefined;
    #variableReferences: VariableReference[] | undefined;

    constructor({
        fillType,
        color,
        gradient,
        opacity,
        blendMode,
        variableReferences
    }: ApiFill) {
        super();

        this.#type = fillType;
        this.#opacity = opacity;
        this.#blendMode = blendMode;
        this.#variableReferences = variableReferences;

        if (fillType === "color") {
            this.#color = new Color(color!);
        } else if (fillType === "gradient") {
            this.#gradient = new Gradient(gradient!);
        } else {
            throw new TypeError(`Unknown fill type: '${fillType}'. Possible values: ${JSON.stringify(fillTypes)}`);
        }
    }

    get type(): FillType {
        return this.#type;
    }

    set type(type: FillType) {
        if (!fillTypes.includes(type)) {
            throw new TypeError(`Unknown fill type: '${type}'. Possible values: ${JSON.stringify(fillTypes)}`);
        }

        this.#type = type;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get color(): Color | undefined {
        return this.#color;
    }

    set color(color: PartialOnly<ColorDefinition, "a">) {
        this.#type = "color";
        this.#color = new Color(color);
        this.#gradient = undefined;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get gradient(): Gradient | undefined {
        return this.#gradient;
    }

    set gradient(gradient: ApiGradient) {
        this.#type = "gradient";
        this.#gradient = new Gradient(gradient);
        this.#color = undefined;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get opacity(): number | undefined {
        return this.#opacity;
    }

    set opacity(opacity: number) {
        if (opacity < 0 || opacity > 1) {
            throw new RangeError(`Invalid opacity value: '${opacity}'. Valid range: [0, 1]`);
        }

        this.#opacity = opacity;
    }

    /* @ts-ignore */ // Ignoring type match rule between get/set
    get blendMode(): ZeplinBlendMode | undefined {
        return this.#blendMode;
    }

    set blendMode(blendMode: ZeplinBlendMode) {
        if (!BLEND_MODES.includes(blendMode)) {
            throw new TypeError(`Unknown blend mode: '${blendMode}'. Possible values: ${JSON.stringify(BLEND_MODES)}`);
        }

        this.#blendMode = blendMode;
    }

    get fill(): Color | Gradient {
        return (this.#type === "color" ? this.color : this.gradient)!;
    }

    get variableReferences() : VariableReference[] | undefined {
        return this.#variableReferences;
    }

    set variableReferences(variableReferences: VariableReference[] | undefined) {
        this.#variableReferences = variableReferences;
    }

    override toObject(): ApiFill {
        const fillObj: ApiFill = {
            fillType: this.#type,
            opacity: this.#opacity,
            blendMode: this.#blendMode,
            variableReferences: this.#variableReferences
        };

        if (this.#color) {
            fillObj.color = this.#color.toObject();
        } else if (this.#gradient) {
            fillObj.gradient = this.#gradient.toObject();
        }

        return fillObj;
    }
}
